import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import { Toaster } from "@/components/ui/sonner";
import { pageImporters } from "@/lib/prefetch";
import { useScrollLockFix } from "@/hooks/useScrollLockFix";

const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const GoogleCallback = lazy(() => import("@/pages/GoogleCallback"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Dashboard = lazy(pageImporters["/dashboard"]);
const Interventi = lazy(pageImporters["/interventi"]);
const Appalti = lazy(pageImporters["/appalti"]);
const TipiLavoro = lazy(pageImporters["/tipi-lavoro"]);
const Dipendenti = lazy(pageImporters["/dipendenti"]);
const Caratterizzazioni = lazy(pageImporters["/caratterizzazioni"]);
const Listini = lazy(pageImporters["/listini"]);
const Utenti = lazy(pageImporters["/utenti"]);
const Inviti = lazy(pageImporters["/inviti"]);
const Reports = lazy(pageImporters["/report"]);
const ReportMensile = lazy(pageImporters["/report-mensile"]);
const ReportDipendenti = lazy(pageImporters["/report-dipendenti"]);
const Presenze = lazy(pageImporters["/presenze"]);
const Impostazioni = lazy(pageImporters["/impostazioni"]);
const Documenti = lazy(pageImporters["/documenti"]);
const Subappalti = lazy(pageImporters["/subappalti"]);
const SubappaltoDetail = lazy(() => import("@/pages/SubappaltoDetail"));

function PageFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<PageFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/interventi" element={<ProtectedRoute><Interventi /></ProtectedRoute>} />
        <Route path="/appalti" element={<ProtectedRoute><Appalti /></ProtectedRoute>} />
        <Route path="/tipi-lavoro" element={<ProtectedRoute><TipiLavoro /></ProtectedRoute>} />
        <Route path="/dipendenti" element={<ProtectedRoute><Dipendenti /></ProtectedRoute>} />
        <Route path="/caratterizzazioni" element={<ProtectedRoute><Caratterizzazioni /></ProtectedRoute>} />
        <Route path="/listini" element={<ProtectedRoute><Listini /></ProtectedRoute>} />
        <Route path="/utenti" element={<ProtectedRoute><Utenti /></ProtectedRoute>} />
        <Route path="/inviti" element={<ProtectedRoute><Inviti /></ProtectedRoute>} />
        <Route path="/report" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/report-mensile" element={<ProtectedRoute><ReportMensile /></ProtectedRoute>} />
        <Route path="/report-dipendenti" element={<ProtectedRoute><ReportDipendenti /></ProtectedRoute>} />
        <Route path="/presenze" element={<ProtectedRoute><Presenze /></ProtectedRoute>} />
        <Route path="/impostazioni" element={<ProtectedRoute><Impostazioni /></ProtectedRoute>} />
        <Route path="/documenti" element={<ProtectedRoute><Documenti /></ProtectedRoute>} />
        <Route path="/subappalti" element={<ProtectedRoute><Subappalti /></ProtectedRoute>} />
        <Route path="/subappalti/:id" element={<ProtectedRoute><SubappaltoDetail /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useScrollLockFix();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
